import { useCallback, useMemo } from 'react';

import {
  PERMISSIONS_HIERARCHY,
  useProfile,
  type FeaturePermission,
} from '@mint-lib/profile-helpers';
import { useFlags } from '@mint-lib/routing-context';

type AiHubFeature = {
  scheduledChatFeature: boolean;
  knowledgeBaseDownloadFeature: boolean;
  knowledgeBaseUploadFeature: boolean;
  knowledgeBaseDeleteFeature: boolean;
  chartDownloadFeature: boolean;
  tableDownloadFeature: boolean;
  reportDownloadFeature: boolean;
};

const permissionConfig: Record<keyof AiHubFeature, FeaturePermission> = {
  scheduledChatFeature: 'modify',
  knowledgeBaseDownloadFeature: 'modify',
  knowledgeBaseUploadFeature: 'modify',
  knowledgeBaseDeleteFeature: 'modify',
  chartDownloadFeature: 'modify',
  tableDownloadFeature: 'modify',
  reportDownloadFeature: 'modify',
};

const useAiHubPermissions = () => {
  const { profileService } = useProfile();
  const ff = useFlags('@mint/arm', ['aiHubPermission']);

  const permissions = profileService.getPermissions();

  const permissionLevelFallback = ff.aiHubPermission.enabled
    ? 'no_access'
    : ('full_control' as FeaturePermission);
  const aiHubPermissionLevel =
    permissions['aiHubFeature'] ?? permissionLevelFallback;

  const hasAccess = useCallback(
    (feature: keyof AiHubFeature) => {
      const currentPermissionIndex =
        PERMISSIONS_HIERARCHY.indexOf(aiHubPermissionLevel);

      const requiredPermissionIndex = PERMISSIONS_HIERARCHY.indexOf(
        permissionConfig[feature] as FeaturePermission,
      );

      if (currentPermissionIndex === -1 || requiredPermissionIndex === -1) {
        return false;
      }

      return currentPermissionIndex >= requiredPermissionIndex;
    },
    [aiHubPermissionLevel],
  );

  return useMemo(
    () =>
      ({
        canScheduleChat: hasAccess('scheduledChatFeature'),
        canDownloadKBFile: hasAccess('knowledgeBaseDownloadFeature'),
        canUploadKBFile: hasAccess('knowledgeBaseUploadFeature'),
        canDeleteKBFile: hasAccess('knowledgeBaseDeleteFeature'),
        canDownloadTable: hasAccess('tableDownloadFeature'),
        canDownloadChart: hasAccess('chartDownloadFeature'),
        canDownloadReport: hasAccess('reportDownloadFeature'),
      }) satisfies Record<string, boolean>,
    [hasAccess],
  );
};

export { useAiHubPermissions };
